import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlineRounded";
import { Box, Button, Stack } from "@mui/material";
import DateRangeSelect from "@entrilia/shared/components/DateRangeSelect";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import MultiSelectFilter from "@entrilia/shared/components/MultiSelectFilter";
import CompaniesIcon from "@entrilia/shared/icons/CompaniesIcon";
import type { DescriptiveDateRange } from "@entrilia/shared/utilities/dateFilterHelper";
import {
  last12Months,
  lastQuarter,
  lastYear,
  thisQuarter,
  thisYear,
} from "@entrilia/shared/utilities/dateFilterHelper";
import type { MessagePublishStatus } from "../../../../api/types/documentCollectionTypes";
import VisibilityIcon from "@entrilia/shared/icons/VisibleIcon";
import DownloadDocumentsButton from "../../../common/DownloadDocumentsButton";
import ExportWithFiltersButton from "../../../common/ExportWithFiltersButton";
import type { FundStructureDocumentsFilterState } from "./fundStructureDocumentsFilters";
import { hasActiveFilters } from "./fundStructureDocumentsFilters";

interface Props {
  categoryOptions: SelectOption[];
  fundOptions?: SelectOption[];
  investorOptions?: SelectOption[];
  filterState: FundStructureDocumentsFilterState;
  onChange: (newFilterState: FundStructureDocumentsFilterState) => void;
  onClear: () => void;
  isLoading?: boolean;
  recordsCount: number;
  totalRecordsCount?: number;
  onExportAllRecords?: () => void;
  onExportFilteredRecords?: () => void;
  isExporting?: boolean;
  onDownloadAllDocuments?: () => void;
  onDownloadFilteredDocuments?: () => void;
  isDownloading?: boolean;
}

interface SelectOption {
  value: string;
  label: string;
}

const statusOptions = [
  { value: "Live", label: "Live" },
  { value: "Preview", label: "Preview" },
];

const FundStructureDocumentFilters = ({
  categoryOptions,
  fundOptions,
  investorOptions,
  filterState,
  onChange,
  onClear,
  isLoading,
  recordsCount,
  totalRecordsCount,
  onExportAllRecords,
  onExportFilteredRecords,
  isExporting,
  onDownloadAllDocuments,
  onDownloadFilteredDocuments,
  isDownloading,
}: Props) => {
  const handleSearch = (searchTerm: string) => onChange({ ...filterState, searchTerm });

  const handleInvestorsChange = (fundInvestorIds: string[]) => onChange({ ...filterState, fundInvestorIds });

  const handleFundsChange = (fundIds: string[]) => onChange({ ...filterState, fundIds });

  const handleCategoriesChange = (categoryIds: string[]) => onChange({ ...filterState, categoryIds });

  const handleStatusesChange = (statuses: string[]) =>
    onChange({ ...filterState, statuses: statuses as MessagePublishStatus[] });

  const handleDateRangeChange = (dateRange: DescriptiveDateRange) => onChange({ ...filterState, dateRange });

  const showClearButton = hasActiveFilters(filterState);

  return (
    <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 2 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mr: 1 }}>
        <Box sx={{ width: "6rem" }}>
          <RecordCounter records={recordsCount} totalRecords={totalRecordsCount} hide={isLoading} />
        </Box>
        {investorOptions && (
          <MultiSelectFilter
            sortOptions
            placeholder="Investor"
            searchable
            options={investorOptions}
            getOptionValue={(o) => o.value}
            getOptionLabel={(o) => o.label}
            selectedValues={filterState.fundInvestorIds}
            onChange={handleInvestorsChange}
            StartIcon={PersonIcon}
            disabled={isLoading}
            width="13rem"
          />
        )}
        {fundOptions && (
          <MultiSelectFilter
            sortOptions
            placeholder="Fund"
            searchable
            options={fundOptions}
            getOptionValue={(o) => o.value}
            getOptionLabel={(o) => o.label}
            selectedValues={filterState.fundIds}
            onChange={handleFundsChange}
            StartIcon={CompaniesIcon}
            disabled={isLoading}
            width="13rem"
          />
        )}
        <MultiSelectFilter
          placeholder="Category"
          options={categoryOptions}
          getOptionValue={(o) => o.value}
          getOptionLabel={(o) => o.label}
          selectedValues={filterState.categoryIds}
          onChange={handleCategoriesChange}
          StartIcon={DescriptionIcon}
          disabled={isLoading}
          width="12rem"
        />
        <MultiSelectFilter
          placeholder="Status"
          options={statusOptions}
          getOptionValue={(o) => o.value}
          getOptionLabel={(o) => o.label}
          selectedValues={filterState.statuses}
          onChange={handleStatusesChange}
          StartIcon={VisibilityIcon}
          disabled={isLoading}
          width="10rem"
        />
        <DateRangeSelect
          predefinedRanges={[last12Months, thisQuarter, thisYear, lastQuarter, lastYear]}
          dateRange={filterState.dateRange}
          onChange={handleDateRangeChange}
          placeholder="Publish Date"
          disabled={isLoading}
          width="13rem"
        />
        {showClearButton && (
          <Button variant="text" onClick={onClear}>
            Clear all
          </Button>
        )}
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <SearchField onSearch={handleSearch} debounceTimeMs={500} />
        {onExportAllRecords && onExportFilteredRecords && (
          <ExportWithFiltersButton
            disabled={isLoading || totalRecordsCount === 0}
            loading={isExporting}
            onExportAllRecords={onExportAllRecords}
            onExportFilteredRecords={onExportFilteredRecords}
          />
        )}
        {onDownloadAllDocuments && onDownloadFilteredDocuments && (
          <DownloadDocumentsButton
            disabled={isLoading}
            loading={isDownloading}
            noFilteredDocuments={recordsCount === 0}
            onDownloadAllDocuments={onDownloadAllDocuments}
            onDownloadFilteredDocuments={onDownloadFilteredDocuments}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default FundStructureDocumentFilters;
